// crewbit-admin/src/components/money/clients/TaxTreatmentSelector.tsx
//
// Tier 2-UI Phase D-admin §WorldClass.1 — paired editor for
// `clients.default_tax_treatment` + `clients.default_tax_rate_override`.
// Mirrors crewbit-mobile/components/money/clients/TaxTreatmentSelector.tsx
// body decisions; web idiom uses native <select> + <input type="number">.
//
// Three modes: 'inherit' (use bfs.hst_rate at resolution time) / 'exempt'
// (force 0) / 'override' (custom rate). 'override' reveals a percent-input
// (UI percent 0..99) that converts to decimal 0..1 at the storage boundary
// per Decision §T2UI.PhaseD-mobile.D5 (mirrors §InvoiceAuthoring.D5).

import { palette, radii, spacing, typography } from '../../../styles/tokens';
import type { TaxTreatment } from '../../../types/clientDefaults';

interface Props {
  treatment: TaxTreatment;
  /** Decimal 0..1 (DB shape). Editor surfaces percent at the input boundary. */
  rateOverride: number | null;
  onChange: (next: { treatment: TaxTreatment; rateOverride: number | null }) => void;
  disabled?: boolean;
}

export default function TaxTreatmentSelector({ treatment, rateOverride, onChange, disabled }: Props) {
  const handleTreatmentChange = (next: TaxTreatment) => {
    if (next === treatment) return;
    // 'inherit' / 'exempt' clear any override; 'override' keeps existing or
    // surfaces a fresh blank input.
    onChange({
      treatment: next,
      rateOverride: next === 'override' ? rateOverride : null,
    });
  };

  const handleRateChange = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      onChange({ treatment: 'override', rateOverride: null });
      return;
    }
    const parsed = Number.parseFloat(trimmed);
    if (Number.isNaN(parsed)) return;
    if (parsed < 0 || parsed > 99) return;
    // UI percent → DB decimal at this boundary.
    onChange({ treatment: 'override', rateOverride: parsed / 100 });
  };

  // DB decimal → UI percent for display.
  const displayPercent = rateOverride === null ? '' : (rateOverride * 100).toString();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
      <select
        value={treatment}
        onChange={(e) => handleTreatmentChange(e.target.value as TaxTreatment)}
        disabled={disabled}
        style={{
          background: palette.white,
          border: `1px solid ${palette.g200}`,
          borderRadius: radii.sm,
          paddingInline: spacing.md,
          paddingBlock: spacing.sm,
          fontSize: typography.size.body,
          fontFamily: typography.fontFamily,
          color: palette.g800,
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="inherit">Inherit business default</option>
        <option value="exempt">Tax-exempt</option>
        <option value="override">Custom rate</option>
      </select>

      {treatment === 'exempt' && (
        <div style={{ fontSize: typography.size.caption, color: palette.g500 }}>
          This client is exempt from tax
        </div>
      )}

      {treatment === 'override' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: spacing.xs }}>
          <div style={{ fontSize: typography.size.caption, color: palette.g600 }}>
            Custom HST rate (%)
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xs,
              background: palette.white,
              border: `1px solid ${palette.g200}`,
              borderRadius: radii.sm,
              paddingInline: spacing.md,
            }}
          >
            <input
              type="number"
              value={displayPercent}
              onChange={(e) => handleRateChange(e.target.value)}
              placeholder="13"
              step="0.1"
              min={0}
              max={99}
              disabled={disabled}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: typography.size.body,
                fontFamily: typography.fontFamily,
                color: disabled ? palette.g400 : palette.g800,
                paddingBlock: spacing.sm,
              }}
            />
            <span style={{ color: palette.g600, fontSize: typography.size.body }}>%</span>
          </div>
        </div>
      )}
    </div>
  );
}
